import { useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';

import { IdentitySuggestionUser, TenantUser } from '../../types/identity';
import RouteFlight from '../RouteFlight';
import TenantLabel from '../TenantLabel';
import TenantUserSearchPanel from './TenantUserSearchPanel';

// FD-381 — the manual add, as ONE surface instead of two sequential dialogs.
//
// The old flow opened `UserPickerDialog` for the source tenant, closed it, then
// opened it again for the destination — and neither dialog named the tenant it
// was searching. So the operator was asked to choose between two directories by
// a screen that never said which one it was showing, twice, with no view of the
// pair being built. Both panels are now on screen at once, each headed by its
// real tenant, with the pending pair spelled out before anything is staged.
//
// D2C IS NOT A T2T PANEL WITH THE LEFT SIDE HIDDEN. `tenant=source` on a D2C
// migration is a hard `400 D2C_HAS_NO_SOURCE_TENANT` — the source identities are
// on-prem AD users and there is no directory to search. Until now that meant D2C
// had no manual mapping path at all (`Add mapping` rendered only for T2T). The
// left panel therefore lists the unmapped source identities ALREADY OBSERVED ON
// DEVICES, which the suggestions feed carries (`sid`, `displayName`, `samName`,
// `deviceCount`) — real facts from submitted manifests rather than a search that
// cannot exist.

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '14px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    backgroundColor: '#ffffff',
    paddingTop: '16px',
    paddingRight: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
  },
  panels: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '20px',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
      rowGap: '20px',
    },
  },
  confirmBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '16px',
    flexWrap: 'wrap',
    rowGap: '10px',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#e4e4e7',
    paddingTop: '12px',
  },
  pair: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    flexWrap: 'wrap',
    fontSize: '13px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
  role: {
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#71717a',
  },
  head: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
  },
  hint: {
    fontSize: '12px',
    color: '#52525b',
  },
  sidList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    maxHeight: '320px',
    overflowY: 'auto',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
  },
});

/** The source half of a pending mapping — a directory user (T2T) or an observed SID (D2C). */
export type PendingSource =
  | { kind: 'tenantUser'; user: TenantUser }
  | { kind: 'observedSid'; sid: string; label: string };

export interface AddMappingPanelProps {
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  sourceTenantDomain?: string | null;
  sourceTenantId?: string | null;
  destTenantDomain?: string | null;
  destTenantId?: string | null;
  /** D2C: source identities seen on devices that have no mapping yet. */
  observedSources?: IdentitySuggestionUser[];
  onCancel: () => void;
  onAdd: (source: PendingSource, destination: TenantUser) => void;
}

export function sourceLabel(source: PendingSource): string {
  return source.kind === 'tenantUser'
    ? source.user.displayName ?? source.user.userPrincipalName ?? source.user.id
    : source.label;
}

export default function AddMappingPanel({
  migrationId,
  migrationType,
  sourceTenantDomain,
  sourceTenantId,
  destTenantDomain,
  destTenantId,
  observedSources = [],
  onCancel,
  onAdd,
}: AddMappingPanelProps) {
  const styles = useStyles();
  const isT2T = migrationType === 'T2T';
  const [source, setSource] = useState<PendingSource | null>(null);
  const [destination, setDestination] = useState<TenantUser | null>(null);

  const complete = source !== null && destination !== null;

  return (
    <div className={styles.wrap} aria-label="Add identity mapping">
      <div className={styles.panels}>
        {isT2T ? (
          <TenantUserSearchPanel
            migrationId={migrationId}
            tenant="source"
            tenantDomain={sourceTenantDomain}
            tenantId={sourceTenantId}
            selectedUserId={source?.kind === 'tenantUser' ? source.user.id : null}
            onSelect={(user) => setSource({ kind: 'tenantUser', user })}
          />
        ) : (
          <div>
            <div className={styles.head}>
              <span className={styles.role}>Source identity (on-premises)</span>
              <span className="font-display text-sm text-zinc-900">
                Observed on this campaign’s devices
              </span>
            </div>
            <p className={`${styles.hint} mt-1 mb-2`}>
              A domain-joined campaign has no source directory to search — these are the
              user SIDs its devices actually reported.
            </p>
            {observedSources.length === 0 ? (
              <MessageBar intent="info">
                <MessageBarBody>
                  <MessageBarTitle>No unmapped source identities yet</MessageBarTitle>
                  Source identities appear once devices submit their manifests.
                </MessageBarBody>
              </MessageBar>
            ) : (
              <div className={styles.sidList}>
                {observedSources.map((row) => {
                  const label = row.displayName ?? row.samName ?? row.userName ?? row.sid;
                  const chosen = source?.kind === 'observedSid' && source.sid === row.sid;
                  return (
                    <button
                      key={row.sid}
                      type="button"
                      onClick={() =>
                        setSource({ kind: 'observedSid', sid: row.sid, label })
                      }
                      className={`flex flex-col items-start px-3 py-2 text-left hover:bg-zinc-50 ${
                        chosen ? 'bg-zinc-100' : ''
                      }`}
                    >
                      <span className="font-medium text-zinc-900 text-[13px]">{label}</span>
                      <span className="font-mono text-[11px] text-zinc-500">{row.sid}</span>
                      <span className="text-[11px] text-zinc-500">
                        {row.deviceCount} device{row.deviceCount === 1 ? '' : 's'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <TenantUserSearchPanel
          migrationId={migrationId}
          tenant="destination"
          tenantDomain={destTenantDomain}
          tenantId={destTenantId}
          selectedUserId={destination?.id ?? null}
          onSelect={setDestination}
        />
      </div>

      <div className={styles.confirmBar}>
        <div className={styles.pair}>
          {complete ? (
            <>
              <span className="font-medium text-zinc-900">{sourceLabel(source)}</span>
              <TenantLabel domain={sourceTenantDomain} tenantId={sourceTenantId} fallback="" />
              <RouteFlight />
              <span className="font-medium text-zinc-900">
                {destination.displayName ?? destination.userPrincipalName ?? destination.id}
              </span>
              <TenantLabel domain={destTenantDomain} tenantId={destTenantId} fallback="" />
            </>
          ) : (
            <span className={styles.hint}>
              {source
                ? 'Now choose the destination identity.'
                : isT2T
                  ? 'Choose a source user, then a destination identity.'
                  : 'Choose a source identity, then a destination identity.'}
            </span>
          )}
        </div>
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            disabled={!complete}
            onClick={() => complete && onAdd(source, destination)}
          >
            Add mapping
          </Button>
        </div>
      </div>
    </div>
  );
}
